const defaultErrorMessage = 'Something went wrong. Please try again.';

const expectedFirebaseAuthCodes = new Set([
  'auth/email-already-in-use',
  'auth/expired-action-code',
  'auth/invalid-credential',
  'auth/invalid-action-code',
  'auth/invalid-email',
  'auth/network-request-failed',
  'auth/popup-closed-by-user',
  'auth/the-service-is-currently-unavailable',
  'auth/too-many-requests',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/wrong-password',
]);

const expectedFirebaseFunctionCodes = new Set([
  'already-exists',
  'functions/already-exists',
  'functions/failed-precondition',
  'functions/invalid-argument',
  'functions/not-found',
  'functions/permission-denied',
  'failed-precondition',
  'invalid-argument',
  'not-found',
  'permission-denied',
]);

const expectedTransientFirebaseCodes = new Set([
  'deadline-exceeded',
  'storage/retry-limit-exceeded',
]);

export class UserFacingError extends Error {
  readonly reportToSentry = false;

  constructor(message: string) {
    super(message);
    this.name = 'UserFacingError';
  }
}

export function shouldReportError(error: unknown) {
  if (
    error instanceof UserFacingError ||
    (error instanceof Error &&
      'reportToSentry' in error &&
      error.reportToSentry === false)
  ) {
    return false;
  }

  if (isShareCancelError(error)) {
    return false;
  }

  if (isExpectedBrowserAbortError(error)) {
    return false;
  }

  if (isPermissionDeniedError(error) || isExpectedUserFacingError(error)) {
    return false;
  }

  if (expectedFirebaseAuthCodes.has(getErrorCode(error))) {
    return false;
  }

  if (expectedFirebaseFunctionCodes.has(getErrorCode(error))) {
    return false;
  }

  if (expectedTransientFirebaseCodes.has(getErrorCode(error))) {
    return false;
  }

  return true;
}

function getErrorCode(error: unknown) {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = error.code;

    if (typeof code === 'string') {
      return code;
    }
  }

  return '';
}

function getRawErrorText(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = error.message;

    if (typeof message === 'string') {
      return message;
    }
  }

  return '';
}

function getErrorName(error: unknown) {
  if (error instanceof Error) {
    return error.name;
  }

  if (error && typeof error === 'object' && 'name' in error) {
    const name = error.name;

    if (typeof name === 'string') {
      return name;
    }
  }

  return '';
}

export function isShareCancelError(error: unknown) {
  const name = getErrorName(error).toLowerCase();
  const message = getRawErrorText(error).toLowerCase();

  return (
    /abort due to cancellation of share|share (was )?cancell?ed/.test(
      message,
    ) ||
    (name === 'aborterror' && /share|cancell?ed|cancellation/.test(message))
  );
}

function isExpectedBrowserAbortError(error: unknown) {
  const name = getErrorName(error).toLowerCase();
  const message = getRawErrorText(error).toLowerCase();

  return (
    name === 'aborterror' ||
    /^aborterror:\s*aborterror\.?$/.test(message) ||
    /^aborterror:\s*the operation was aborted\.?$/.test(message) ||
    /^the operation was aborted\.?$/.test(message)
  );
}

function isExpectedUserFacingError(error: unknown) {
  const name = getErrorName(error).toLowerCase();
  const message = getRawErrorText(error).toLowerCase();

  return (
    (name === 'notsupportederror' &&
      /^the operation is not supported\.?$/.test(message)) ||
    /^add .+ before (posting|saving)/i.test(message) ||
    /^browser notification permission was not granted\.?$/i.test(message) ||
    /^clipboard copy is not available on this device\.?$/i.test(message) ||
    /^connect to the internet to finish onboarding\.?$/i.test(message) ||
    /^make a change before saving/i.test(message) ||
    /^photos must be /i.test(message) ||
    /^selected file must be /i.test(message) ||
    /^your email is not verified yet\.?$/i.test(message) ||
    /^your browser blocked clipboard access/i.test(message) ||
    /^videos must be /i.test(message)
  );
}

export function isPermissionDeniedError(error: unknown) {
  return (
    getErrorCode(error) === 'permission-denied' ||
    (error instanceof Error &&
      /missing or insufficient permissions/i.test(error.message))
  );
}

export function getErrorMessage(
  error: unknown,
  fallback = defaultErrorMessage,
) {
  const errorCode = getErrorCode(error);

  if (
    errorCode === 'auth/invalid-credential' ||
    errorCode === 'auth/user-not-found' ||
    errorCode === 'auth/wrong-password'
  ) {
    return 'Email or password is incorrect.';
  }

  if (errorCode === 'auth/invalid-email') {
    return 'Enter a valid email address.';
  }

  if (errorCode === 'auth/email-already-in-use') {
    return 'That email already has an account. Sign in instead or reset your password.';
  }

  if (errorCode === 'auth/network-request-failed') {
    return 'We could not reach Firebase. Check your connection and try again.';
  }

  if (errorCode === 'auth/the-service-is-currently-unavailable') {
    return 'Firebase is temporarily unavailable. Wait a moment, then try again.';
  }

  if (errorCode === 'auth/too-many-requests') {
    return 'Too many attempts. Wait a few minutes, then try again.';
  }

  if (errorCode === 'auth/user-disabled') {
    return 'This account has been disabled.';
  }

  if (error instanceof Error && error.message.trim()) {
    if (/^internal$/i.test(error.message.trim())) {
      return fallback;
    }

    if (/missing or insufficient permissions/i.test(error.message)) {
      return (
        (fallback !== defaultErrorMessage ? fallback : null) ||
        'We could not save that change because the account write was rejected. Please refresh and try again.'
      );
    }

    return error.message;
  }

  return fallback;
}
